import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import {
  createCampaign,
  launchCampaign,
  saveCampaign,
  updateCampaign,
} from "./features/ownerActions";
import { setCampaigns } from "./slices/ownerSlice";
import { fetchCampaigns } from "../utils";

/**
 * Keeps `owner.campaigns` in sync after mutations without refetching on every route change.
 * Matches scope resolution used in DefaultLayout / dashboard.
 */
export const campaignsRefreshMiddleware = createListenerMiddleware();

/** Narrow slice shape for refresh; avoids pulling full RootState (and matches resolveUserId). */
type OwnerBranchForCampaignsRefresh = {
  owner?: { _id?: string; userId?: string | { _id?: string } } | null;
  currentUser?: { userId?: string } | null;
  currentWorkspace?: unknown;
};

const resolveWorkspaceId = (currentWorkspace: unknown): string => {
  const workspace =
    typeof currentWorkspace === "string"
      ? (() => {
          try {
            return JSON.parse(currentWorkspace || "null");
          } catch {
            return null;
          }
        })()
      : currentWorkspace;
  return String((workspace as { _id?: string })?._id || "").trim();
};

const resolveUserId = (state: { owner: OwnerBranchForCampaignsRefresh }): string => {
  const rawUserId =
    state.owner.currentUser?.userId || state.owner.owner?.userId;
  if (!rawUserId) return "";
  if (typeof rawUserId === "string") return rawUserId.trim();
  if (typeof rawUserId === "object" && rawUserId && "_id" in rawUserId) {
    return String((rawUserId as { _id: string })._id).trim();
  }
  return "";
};

campaignsRefreshMiddleware.startListening({
  matcher: isAnyOf(
    createCampaign.fulfilled,
    saveCampaign.fulfilled,
    launchCampaign.fulfilled,
    updateCampaign.fulfilled
  ),
  effect: async (_action, listenerApi) => {
    const state = listenerApi.getState() as { owner: OwnerBranchForCampaignsRefresh };
    const ownerId = String(state.owner.owner?._id || "").trim();
    if (!ownerId) return;

    const userId = resolveUserId(state);
    const workspaceId = resolveWorkspaceId(state.owner.currentWorkspace);

    try {
      const campaigns = await fetchCampaigns(
        ownerId,
        userId || undefined,
        workspaceId || undefined
      );
      listenerApi.dispatch(setCampaigns(Array.isArray(campaigns) ? campaigns : []));
    } catch {
      // Best-effort; keep existing list on failure.
    }
  },
});
